import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, BellRing, CheckCircle2, CircleAlert, Copy, Play, RefreshCw, RotateCcw, Send, Trash2, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { AlertsCard } from "@/components/AlertsCard";
import { Badge, Button, Card, cx, Input, Skeleton } from "@/components/ui";
import { api, ApiError, type CheckResult } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";
import { McpAdminCard } from "@/components/McpCards";

const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Fehler") : "Fehler");
const TONE = { ok: "text-success", warning: "text-warning", critical: "text-danger" } as const;

/** Logiful intern: Systemstatus – Prüfungen, offene Störungen, Warteschlangen, gescheiterte Jobs, Alarmkanäle */
export function AdminSystemPage() {
  const { me } = useTenant();
  const admin = me.user.staffRole === "staff_admin";
  const qc = useQueryClient();
  const st = useQuery({ queryKey: ["system-status"], queryFn: api.admin.systemStatus, refetchInterval: 30_000 });
  const run = useMutation({
    mutationFn: api.admin.runChecks,
    onSuccess: (r) => { toast.success(r.ran ? "Prüfungen ausgeführt" : "Läuft gerade auf einer anderen Instanz"); void qc.invalidateQueries({ queryKey: ["system-status"] }); void qc.invalidateQueries({ queryKey: ["alerts"] }); },
    onError: (e) => toast.error("Nicht ausgeführt", { description: errMsg(e) }),
  });
  const results = st.data?.lastRun?.results ?? [];
  const worst = results.some((r) => r.status === "critical") ? "critical" : results.some((r) => r.status === "warning") ? "warning" : "ok";

  return (
    <div className="pb-16">
      <PageHeader title="Systemstatus" description="Prüft jede Minute, ob alles läuft – auch stille Ausfälle. Störungen gehen per E-Mail (und optional Webhook) an die Logiful-Admins." actions={<Button icon={<Play className="size-4" />} loading={run.isPending} onClick={() => run.mutate()}>Jetzt prüfen</Button>} />
      <div className="px-4 md:px-8 space-y-6 max-w-5xl">
        {!st.data ? <Skeleton className="h-24" /> : (
          <Card className={cx("p-4 flex items-center gap-3", worst === "critical" ? "border-danger/40 bg-danger-soft/30" : worst === "warning" ? "border-warning/40 bg-warning-soft/30" : "border-success/30 bg-success-soft/30")}>
            {worst === "ok" ? <CheckCircle2 className="size-6 text-success" /> : worst === "warning" ? <TriangleAlert className="size-6 text-warning" /> : <CircleAlert className="size-6 text-danger" />}
            <div>
              <p className="font-semibold">{worst === "ok" ? "Alles läuft" : worst === "warning" ? "Auffälligkeiten" : "Kritische Störung"}</p>
              <p className="text-[12.5px] text-fg-muted">{st.data.lastRun ? `Zuletzt geprüft ${fmtRelative(st.data.lastRun.at)}` : "Noch nicht geprüft – startet kurz nach dem Deploy."}{st.data.heartbeat ? ` · Worker ${st.data.heartbeat.host}, Lebenszeichen vor ${st.data.heartbeat.ageSec} s` : " · kein Lebenszeichen vom Worker"}</p>
            </div>
          </Card>
        )}

        <AlertsCard />

        <McpAdminCard />

        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-border font-semibold text-[14px] flex items-center gap-2"><Activity className="size-4 text-fg-muted" /> Prüfungen</div>
          <ul className="divide-y divide-border">
            {results.map((r: CheckResult) => (
              <li key={r.key} className="px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[13px]">
                <span className={cx("size-2.5 rounded-full shrink-0", r.status === "ok" ? "bg-success" : r.status === "warning" ? "bg-warning" : "bg-danger")} />
                <span className="font-medium sm:w-60 shrink-0">{r.label}</span>
                <span className={cx("min-w-0 basis-full pl-5 sm:pl-0 sm:basis-0 sm:flex-1 break-words", r.status === "ok" ? "text-fg-muted" : TONE[r.status])}>{r.detail}</span>
              </li>
            ))}
            {!results.length && <li className="px-4 py-4 text-[13px] text-fg-muted">Noch keine Ergebnisse.</li>}
          </ul>
        </Card>

        {st.data && st.data.queues.length > 0 && (
          <Card className="overflow-hidden">
            <div className="px-4 py-3 border-b border-border font-semibold text-[14px]">Warteschlangen</div>
            <table className="w-full text-[13px]">
              <thead className="text-[12px] text-fg-subtle"><tr><th className="text-left font-medium px-4 py-2">Warteschlange</th><th className="text-right font-medium px-3">wartend</th><th className="text-right font-medium px-3">aktiv</th><th className="text-right font-medium px-3">geplant</th><th className="text-right font-medium px-4">gescheitert (14 T.)</th></tr></thead>
              <tbody>
                {st.data.queues.map((q) => (
                  <tr key={q.key} className="border-t border-border">
                    <td className="px-4 py-2 font-medium">{q.label}{q.error && <span className="block text-[11.5px] text-danger">{q.error}</span>}</td>
                    <td className={cx("text-right px-3 tabular-nums", q.waiting > 500 && "text-warning font-medium")}>{fmtNumber(q.waiting)}</td>
                    <td className="text-right px-3 tabular-nums">{fmtNumber(q.active)}</td>
                    <td className="text-right px-3 tabular-nums">{fmtNumber(q.delayed)}</td>
                    <td className={cx("text-right px-4 tabular-nums", q.failed > 0 && "text-warning")}>{fmtNumber(q.failed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        <FailedJobs admin={admin} />
        <AlertChannels admin={admin} />
      </div>
    </div>
  );
}

function FailedJobs({ admin }: { admin: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["failed-jobs"], queryFn: api.admin.failedJobs });
  const [all, setAll] = useState(false);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["failed-jobs"] }); void qc.invalidateQueries({ queryKey: ["system-status"] }); };
  const retry = useMutation({ mutationFn: (j: { queue: string; id: string }) => api.admin.retryJob(j.queue, j.id), onSuccess: () => { toast.success("Erneut gestartet"); refresh(); }, onError: (e) => toast.error("Nicht gestartet", { description: errMsg(e) }) });
  const drop = useMutation({ mutationFn: (j: { queue: string; id: string }) => api.admin.removeJob(j.queue, j.id), onSuccess: () => { toast.success("Verworfen"); refresh(); }, onError: (e) => toast.error("Nicht verworfen", { description: errMsg(e) }) });
  const list = q.data ?? [];
  const shown = all ? list : list.slice(0, 15);
  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <span className="font-semibold text-[14px]">Gescheiterte Jobs</span>
        <span className="text-[12px] text-fg-subtle">alle Versuche verbraucht · 14 Tage aufbewahrt</span>
        <button onClick={() => void q.refetch()} className="ml-auto size-7 grid place-items-center rounded-md hover:bg-surface-2 text-fg-subtle" aria-label="Neu laden"><RefreshCw className={cx("size-3.5", q.isFetching && "animate-spin")} /></button>
      </div>
      {q.isLoading ? <div className="p-4"><Skeleton className="h-16" /></div> : !list.length ? (
        <p className="px-4 py-5 text-[13px] text-fg-muted text-center">Keine gescheiterten Jobs.</p>
      ) : (
        <ul className="divide-y divide-border">
          {shown.map((j) => (
            <li key={`${j.queue}:${j.id}`} className="px-4 py-2.5 flex items-start gap-3 text-[13px]">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2"><span className="font-medium font-mono text-[12.5px]">{j.name}</span><Badge>{j.queueLabel}</Badge><span className="text-[11.5px] text-fg-subtle">{j.finishedOn ? fmtRelative(j.finishedOn) : ""} · {j.attemptsMade} Versuche</span></div>
                <p className="text-[12px] text-danger break-words">{j.failedReason}</p>
                <p className="text-[11px] text-fg-subtle font-mono truncate">{j.data}</p>
              </div>
              {admin && (
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" icon={<RotateCcw className="size-3.5" />} loading={retry.isPending && retry.variables?.id === j.id} onClick={() => retry.mutate(j)}>Erneut</Button>
                  <button onClick={() => drop.mutate(j)} className="size-8 grid place-items-center rounded-md text-fg-subtle hover:text-danger hover:bg-surface-2" aria-label="Verwerfen" title="Verwerfen"><Trash2 className="size-3.5" /></button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {list.length > 15 && <button onClick={() => setAll(!all)} className="w-full py-2 border-t border-border text-[12.5px] text-fg-muted hover:text-fg">{all ? "Weniger anzeigen" : `Alle ${list.length} anzeigen`}</button>}
    </Card>
  );
}

function AlertChannels({ admin }: { admin: boolean }) {
  const qc = useQueryClient();
  const st = useQuery({ queryKey: ["system-status"], queryFn: api.admin.systemStatus });
  const [url, setUrl] = useState("");
  const save = useMutation({
    mutationFn: (u: string | null) => api.admin.setAlertWebhook(u),
    onSuccess: (r) => { toast.success(r.configured ? "Webhook gespeichert" : "Webhook entfernt"); setUrl(""); void qc.invalidateQueries({ queryKey: ["system-status"] }); },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e) }),
  });
  const test = useMutation({
    mutationFn: api.admin.alertTest,
    onSuccess: (r) => toast[r.webhook === "failed" ? "error" : "success"](r.webhook === "failed" ? "Webhook fehlgeschlagen" : "Testalarm verschickt", { description: `${r.emails} E-Mail(s)${r.webhook === "ok" ? " + Webhook" : ""}${r.webhookError ? ` · ${r.webhookError}` : ""}` }),
    onError: (e) => toast.error("Nicht verschickt", { description: errMsg(e) }),
  });
  const deep = `${location.origin}/health/deep`;
  const c = st.data?.channels;
  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-start gap-3">
        <BellRing className="size-4 text-fg-subtle mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-[13.5px]">Alarmkanäle</p>
          <p className="text-[12.5px] text-fg-muted">Kritische Störungen sofort, Warnungen ab dem zweiten Auftreten – stündlich wiederholt, bis behoben; danach eine Entwarnung.</p>
        </div>
        {admin && <Button size="sm" icon={<Send className="size-3.5" />} loading={test.isPending} onClick={() => test.mutate()}>Testalarm</Button>}
      </div>
      <div className="text-[13px]">
        <p className="font-medium">E-Mail an alle Logiful-Admins</p>
        <p className="text-fg-muted text-[12.5px]">{c?.emails.length ? c.emails.join(", ") : "keine Admins hinterlegt"}</p>
      </div>
      <div className="text-[13px] space-y-1.5">
        <p className="font-medium">Slack, Microsoft Teams oder Google Chat <span className="font-normal text-fg-subtle">(optional)</span></p>
        {c?.webhook.configured ? (
          <div className="flex items-center gap-2"><Badge tone="success">verbunden</Badge><span className="text-fg-muted text-[12.5px]">{c.webhook.host}</span>{admin && <Button size="sm" variant="ghost" className="ml-auto" onClick={() => save.mutate(null)}>Entfernen</Button>}</div>
        ) : admin ? (
          <div className="flex gap-2"><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://hooks.slack.com/services/…" className="flex-1" /><Button size="sm" variant="primary" disabled={!url.trim()} loading={save.isPending} onClick={() => save.mutate(url.trim())}>Speichern</Button></div>
        ) : <p className="text-fg-muted text-[12.5px]">nicht eingerichtet</p>}
        <p className="text-[11.5px] text-fg-subtle">„Incoming Webhook“ im Kanal anlegen und die Adresse hier eintragen – sie wird verschlüsselt gespeichert.</p>
      </div>
      <div className="text-[13px] space-y-1">
        <p className="font-medium">Überwachung von außen <span className="font-normal text-fg-subtle">(empfohlen)</span></p>
        <p className="text-[12.5px] text-fg-muted">Fällt die API selbst aus, kann sie keinen Alarm schicken. Ein externer Dienst (z. B. Better Stack, UptimeRobot) sollte alle 1–5 Minuten diese Adresse abrufen und bei Status 503 oder keiner Antwort alarmieren:</p>
        <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-2/50 px-2.5 py-1.5 font-mono text-[12px]">
          <span className="truncate flex-1">{deep}</span>
          <button onClick={() => void navigator.clipboard.writeText(deep).then(() => toast.success("Kopiert"))} className="text-fg-subtle hover:text-fg" aria-label="Kopieren"><Copy className="size-3.5" /></button>
        </div>
      </div>
    </Card>
  );
}
